import { toast } from "sonner";
import { gtmException } from "@/lib/gtmDataLayer";

// Shopify Storefront API Configuration
export const SHOPIFY_API_VERSION = '2025-07';
export const SHOPIFY_STORE_PERMANENT_DOMAIN = 'zooi-se.myshopify.com';
export const SHOPIFY_STOREFRONT_URL = `https://${SHOPIFY_STORE_PERMANENT_DOMAIN}/api/${SHOPIFY_API_VERSION}/graphql.json`;
export const SHOPIFY_STOREFRONT_TOKEN = 'b8f4075647a864eae69c0571875425bd';

// Types
export interface ShopifyProduct {
  node: {
    id: string;
    title: string;
    description: string;
    handle: string;
    vendor?: string;
    inventoryQuantity?: number | null;
    priceRange: {
      minVariantPrice: {
        amount: string;
        currencyCode: string;
      };
    };
    compareAtPriceRange?: {
      maxVariantPrice: {
        amount: string;
        currencyCode: string;
      };
    };
    images: {
      edges: Array<{
        node: {
          url: string;
          altText: string | null;
        };
      }>;
    };
    variants: {
      edges: Array<{
        node: {
          id: string;
          title: string;
          price: {
            amount: string;
            currencyCode: string;
          };
          availableForSale: boolean;
          inventoryQuantity?: number | null;
          selectedOptions: Array<{
            name: string;
            value: string;
          }>;
          weight?: number;
          weightUnit?: string;
        };
      }>;
    };
    options: Array<{
      name: string;
      values: string[];
    }>;
    kortDatum?: {
      value: string;
    } | null;
    tags?: string[];
    shopifyCreatedAt?: string;
  };
}

export interface CartItem {
  lineId: string | null;
  product: ShopifyProduct;
  variantId: string;
  variantTitle: string;
  /**
   * Shopify product handle — mirrors `product.node.handle`, surfaced as a
   * top-level field so ecommerce events (add_to_cart, begin_checkout,
   * purchase) can emit `item_id` aligned with the GMC feed offerId without
   * traversing the nested product structure (which gets stripped to keep
   * localStorage small in `compactCartItem`).
   *
   * Always populated by `compactCartItem`. Use `cartItemToFeedOfferId(item)`
   * from `@/lib/feedOfferId` to convert this into the feed-aligned id.
   */
  productHandle?: string;
  price: {
    amount: string;
    currencyCode: string;
  };
  quantity: number;
  selectedOptions: Array<{
    name: string;
    value: string;
  }>;
  maxQuantity?: number;
  /** Variant weight in grams (from Shopify Storefront API) */
  weightGrams?: number;
}


// GraphQL Queries
export const STOREFRONT_PRODUCTS_QUERY = `
  query GetProducts($first: Int!, $query: String) {
    products(first: $first, query: $query) {
      edges {
        node {
          id
          title
          description
          handle
          vendor
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          compareAtPriceRange {
            maxVariantPrice {
              amount
              currencyCode
            }
          }
          images(first: 5) {
            edges {
              node {
                url
                altText
              }
            }
          }
          variants(first: 10) {
            edges {
              node {
                id
                title
                price {
                  amount
                  currencyCode
                }
                availableForSale
                selectedOptions {
                  name
                  value
                }
                weight
                weightUnit
              }
            }
          }
          options {
            name
            values
          }
          kortDatum: metafield(namespace: "custom", key: "kort_datum") {
            value
          }
        }
      }
    }
  }
`;

export const STOREFRONT_COLLECTION_PRODUCTS_QUERY = `
  query GetCollectionProducts($handle: String!, $first: Int!) {
    collectionByHandle(handle: $handle) {
      id
      title
      description
      products(first: $first, sortKey: BEST_SELLING) {
        edges {
          node {
            id
            title
            description
            handle
            vendor
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            compareAtPriceRange {
              maxVariantPrice {
                amount
                currencyCode
              }
            }
            images(first: 5) {
              edges {
                node {
                  url
                  altText
                }
              }
            }
            variants(first: 10) {
              edges {
                node {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  availableForSale
                  selectedOptions {
                    name
                    value
                  }
                  weight
                  weightUnit
                }
              }
            }
            options {
              name
              values
            }
            kortDatum: metafield(namespace: "custom", key: "kort_datum") {
              value
            }
          }
        }
      }
    }
  }
`;

export const STOREFRONT_PRODUCT_BY_HANDLE_QUERY = `
  query GetProductByHandle($handle: String!) {
    productByHandle(handle: $handle) {
      id
      title
      description
      handle
      vendor
      priceRange {
        minVariantPrice {
          amount
          currencyCode
        }
      }
      compareAtPriceRange {
        maxVariantPrice {
          amount
          currencyCode
        }
      }
      images(first: 10) {
        edges {
          node {
            url
            altText
          }
        }
      }
      variants(first: 50) {
        edges {
          node {
            id
            title
            price {
              amount
              currencyCode
            }
            availableForSale
            selectedOptions {
              name
              value
            }
            weight
            weightUnit
          }
        }
      }
      options {
        name
        values
      }
      kortDatum: metafield(namespace: "custom", key: "kort_datum") {
        value
      }
    }
  }
`;

const CART_CREATE_MUTATION = `
  mutation cartCreate($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
        totalQuantity
        lines(first: 100) {
          edges {
            node {
              id
              merchandise {
                ... on ProductVariant {
                  id
                }
              }
            }
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

const CART_LINES_ADD_MUTATION = `
  mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        id
        checkoutUrl
        lines(first: 100) {
          edges {
            node {
              id
              merchandise {
                ... on ProductVariant {
                  id
                }
              }
            }
          }
        }
      }
      userErrors { field message }
    }
  }
`;

const CART_LINES_UPDATE_MUTATION = `
  mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart { id }
      userErrors { field message }
    }
  }
`;

const CART_LINES_REMOVE_MUTATION = `
  mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart { id }
      userErrors { field message }
    }
  }
`;

const CART_QUERY = `
  query cart($id: ID!) {
    cart(id: $id) {
      id
      totalQuantity
      checkoutUrl
      lines(first: 100) {
        edges {
          node {
            id
            quantity
            merchandise {
              ... on ProductVariant {
                id
              }
            }
          }
        }
      }
    }
  }
`;

const CART_BUYER_IDENTITY_UPDATE = `
  mutation cartBuyerIdentityUpdate($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
    cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
      cart { id checkoutUrl }
      userErrors { field message }
    }
  }
`;

// Tvinga Shopify-cart att använda en specifik delivery option per delivery group.
// Detta är den ENDA officiella vägen i Storefront API att välja shipping line —
// priset bestäms av Shopifys shipping-profiler/carrier rates, inte av klienten.
const CART_SELECTED_DELIVERY_OPTIONS_UPDATE = `
  mutation cartSelectedDeliveryOptionsUpdate(
    $cartId: ID!
    $selectedDeliveryOptions: [CartSelectedDeliveryOptionInput!]!
  ) {
    cartSelectedDeliveryOptionsUpdate(
      cartId: $cartId
      selectedDeliveryOptions: $selectedDeliveryOptions
    ) {
      cart {
        id
        checkoutUrl
        deliveryGroups(first: 10, withCarrierRates: true) {
          edges {
            node {
              id
              groupType
              selectedDeliveryOption {
                handle
                title
                code
                deliveryMethodType
                estimatedCost { amount currencyCode }
              }
              deliveryOptions {
                handle
                title
                code
                deliveryMethodType
                estimatedCost { amount currencyCode }
              }
            }
          }
        }
      }
      userErrors { field message }
    }
  }
`;

// Storefront API helper function
export async function storefrontApiRequest(query: string, variables: Record<string, unknown> = {}) {
  const response = await fetch(SHOPIFY_STOREFRONT_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': SHOPIFY_STOREFRONT_TOKEN
    },
    body: JSON.stringify({
      query,
      variables,
    }),
  });

  if (response.status === 402) {
    gtmException('shopify_api_402_payment_required', true);
    toast.error("Shopify: Betalning krävs", {
      description: "Shopify API-åtkomst kräver en aktiv Shopify-plan. Besök admin.shopify.com för att uppgradera.",
    });
    return null;
  }

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  
  if (data.errors) {
    throw new Error(`Error calling Shopify: ${data.errors.map((e: { message: string }) => e.message).join(', ')}`);
  }

  return data;
}

// Fetch products from Shopify
export async function fetchShopifyProducts(first: number = 20, query?: string): Promise<ShopifyProduct[]> {
  try {
    const data = await storefrontApiRequest(STOREFRONT_PRODUCTS_QUERY, { first, query });
    if (!data) return [];
    return data.data.products.edges || [];
  } catch (error) {
    console.error('Error fetching Shopify products:', error);
    gtmException('fetch_products_failed', false);
    toast.error("Kunde inte hämta produkter", {
      description: "Ett fel uppstod när produkterna skulle hämtas från Shopify.",
    });
    return [];
  }
}

// Known L1 animal slugs for post-filtering
const KNOWN_L1_SLUGS = ['hund', 'katt', 'smadjur', 'fagel', 'akvarium', 'reptil', 'hast'] as const;

// Post-filter collection products to ensure they belong to the correct L1 category
async function filterByL1Tag(products: ShopifyProduct[], l1: string): Promise<ShopifyProduct[]> {
  if (!l1 || !(KNOWN_L1_SLUGS as readonly string[]).includes(l1)) return products;
  try {
    const { supabase } = await import('@/integrations/supabase/client');
    const handles = products.map(p => p.node.handle);
    const { data: dbProducts } = await supabase
      .from('shopify_products')
      .select('handle, tags')
      .in('handle', handles);
    
    if (!dbProducts) return products;
    
    const handleTagMap = new Map(dbProducts.map(p => [p.handle, p.tags || []]));
    
    return products.filter(p => {
      const tags = handleTagMap.get(p.node.handle);
      if (!tags) return true; // Keep if not in DB (shouldn't happen)
      // Product must have the correct L1 tag, OR no L1 tags at all
      const l1Tags = tags.filter((t: string) => t.startsWith('L1:'));
      if (l1Tags.length === 0) return true;
      return l1Tags.includes(`L1:${l1}`);
    });
  } catch (err) {
    console.warn('L1 post-filter failed:', err);
    return products;
  }
}

// Fetch products from a Shopify collection by handle, with fallback to search query
export async function fetchShopifyCollectionProducts(handle: string, first: number = 20): Promise<ShopifyProduct[]> {
  // Extract L1 from handle for post-filtering
  const handleParts = handle.split('-');
  const potentialL1 = handleParts[0];
  const needsL1Filter = (KNOWN_L1_SLUGS as readonly string[]).includes(potentialL1) && handleParts.length >= 2;
  const isPureL1 = (KNOWN_L1_SLUGS as readonly string[]).includes(handle);

  // For pure L1 handles (e.g. "katt", "hund"), use DB-based fetching
  // to get properly tagged products and prioritize category-exclusive ones
  if (isPureL1) {
    try {
      const { supabase } = await import('@/integrations/supabase/client');
      const l1Tag = `L1:${handle}`;
      
      // Fetch products tagged with this L1
      const { data: dbProducts } = await supabase
        .from('shopify_products')
        .select('handle, tags')
        .contains('tags', [l1Tag])
        .eq('available_for_sale', true)
        .limit(first * 2);
      
      if (dbProducts && dbProducts.length > 0) {
        // Sort: products exclusive to this L1 first, multi-category last
        const sorted = dbProducts.sort((a, b) => {
          const aL1Tags = (a.tags || []).filter((t: string) => t.startsWith('L1:'));
          const bL1Tags = (b.tags || []).filter((t: string) => t.startsWith('L1:'));
          const aExclusive = aL1Tags.length === 1;
          const bExclusive = bL1Tags.length === 1;
          if (aExclusive && !bExclusive) return -1;
          if (!aExclusive && bExclusive) return 1;
          return 0;
        });
        
        const handles = sorted.slice(0, first).map(p => p.handle);
        const allProducts: ShopifyProduct[] = [];
        
        const batchSize = 5;
        for (let i = 0; i < handles.length; i += batchSize) {
          const batch = handles.slice(i, i + batchSize);
          const promises = batch.map(async (h: string) => {
            try {
              const product = await fetchShopifyProductByHandle(h);
              if (product) allProducts.push({ node: product });
            } catch {}
          });
          await Promise.all(promises);
        }
        
        // L1 DB fetch complete
        if (allProducts.length > 0) return allProducts;
      }
    } catch (err) {
      console.warn(`L1 DB fetch failed for "${handle}":`, err);
    }
    // Fall through to collection fetch if DB fails
  }

  try {
    const data = await storefrontApiRequest(STOREFRONT_COLLECTION_PRODUCTS_QUERY, { handle, first: needsL1Filter ? first * 2 : first });
    if (!data) return [];
    const collection = data.data.collectionByHandle;
    const products = collection?.products?.edges || [];
    if (!collection || products.length === 0) {
      console.warn(`Collection ${handle} ${!collection ? 'not found' : 'empty'}, falling back to DB tag lookup`);
      const parts = handle.split('-');
      if (parts.length >= 2) {
        const l1 = parts[0];
        
        // Known L2 slugs to detect handle structure
        const KNOWN_L2_SLUGS = new Set([
          'mat-godis', 'leksaker', 'sova-vila', 'koppel-halsband', 'godis-tugg',
          'klosmobler', 'toalett-hygien', 'sova-mysa', 'transport', 'vard-halsa',
          'burar-inredning', 'stro-hygien', 'mat-foder', 'terrarier-teknik',
          'akvarier-teknik', 'fiskmat', 'inredning', 'leksaker-aktivering',
          'foder', 'godis-snacks', 'tugg-dental', 'matplats', 'klader-uteliv',
          'transport-sakerhet', 'rykt-vard', 'foder-godis', 'ridning-utrustning',
          'stall-hagn', 'vard-tillbehor', 'tillbehor',
        ]);
        
        // Parse L2 and L3 from handle
        let l2Slug = '';
        let l3Slug = '';
        for (let i = parts.length; i >= 2; i--) {
          const candidate = parts.slice(1, i).join('-');
          if (KNOWN_L2_SLUGS.has(candidate)) {
            l2Slug = candidate;
            l3Slug = i < parts.length ? parts.slice(i).join('-') : '';
            break;
          }
        }
        if (!l2Slug) {
          // No known L2 found - treat rest as single segment
          l2Slug = parts.slice(1).join('-');
        }
        
        try {
          const { supabase } = await import('@/integrations/supabase/client');
          let dbProducts: { handle: string }[] | null = null;
          
          if (l3Slug) {
            // DB fallback: L1 + L2 + L3
            const { data } = await supabase
              .from('shopify_products')
              .select('handle')
              .contains('tags', [`L1:${l1}`, `L2:${l2Slug}`, `L3:${l3Slug}`])
              .limit(first);
            dbProducts = data;
          }
          
          if (!dbProducts || dbProducts.length === 0) {
            // DB fallback: L1 + L2
            const { data } = await supabase
              .from('shopify_products')
              .select('handle')
              .contains('tags', [`L1:${l1}`, `L2:${l2Slug}`])
              .limit(first);
            dbProducts = data;
          }
          
          if (!dbProducts || dbProducts.length === 0) {
            const lastPart = parts[parts.length - 1];
            // DB fallback: L1 + L3
            const { data } = await supabase
              .from('shopify_products')
              .select('handle')
              .contains('tags', [`L1:${l1}`, `L3:${lastPart}`])
              .limit(first);
            dbProducts = data;
          }
          
          if (dbProducts && dbProducts.length > 0) {
            // DB fallback: fetching matched handles
            const handles = dbProducts.map((p: { handle: string }) => p.handle);
            const allMatchedProducts: ShopifyProduct[] = [];
            
            const batchSize = 5;
            for (let i = 0; i < Math.min(handles.length, first); i += batchSize) {
              const batch = handles.slice(i, i + batchSize);
              const promises = batch.map(async (h: string) => {
                try {
                  const product = await fetchShopifyProductByHandle(h);
                  if (product) {
                    allMatchedProducts.push({ node: product });
                  }
                } catch {}
              });
              await Promise.all(promises);
            }
            
            // DB fallback complete
            if (allMatchedProducts.length > 0) return allMatchedProducts;
          }
          
          // No matching products found - return empty instead of irrelevant generic results
          // DB fallback: no products found, returning empty
          return [];
        } catch (dbError) {
          console.warn('DB fallback failed:', dbError);
        }
        // DB fallback failed entirely - return empty rather than generic search
        return [];
      }
      return [];
    }
    const mapped = products.map((edge: { node: ShopifyProduct["node"] }) => ({ node: edge.node }));
    if (needsL1Filter) {
      const filtered = await filterByL1Tag(mapped, potentialL1);
      // L1 post-filter applied
      return filtered.slice(0, first);
    }
    return mapped;
  } catch (error) {
    console.error('Error fetching collection products:', error);
    gtmException('fetch_collection_failed', false);
    return [];
  }
}

// Fetch single product by handle
export async function fetchShopifyProductByHandle(handle: string) {
  try {
    const data = await storefrontApiRequest(STOREFRONT_PRODUCT_BY_HANDLE_QUERY, { handle });
    if (!data) return null;
    return data.data.productByHandle;
  } catch (error) {
    console.error('Error fetching Shopify product:', error);
    gtmException('fetch_product_failed', false);
    return null;
  }
}

// ── DB-based product fetching (no Storefront API dependency) ──

/**
 * Convert a shopify_products DB row to the ShopifyProduct shape used by all components.
 */
export function dbToShopifyProduct(row: {
  id: string;
  shopify_id: string;
  title: string;
  handle: string;
  description?: string | null;
  vendor?: string | null;
  price?: number | null;
  compare_at_price?: number | null;
  currency?: string | null;
  image_url?: string | null;
  images?: unknown;
  variants?: unknown;
  options?: unknown;
  available_for_sale?: boolean | null;
  inventory_quantity?: number | null;
  tags?: string[] | null;
}): ShopifyProduct {
  const currency = row.currency || "SEK";
  const price = String(row.price ?? 0);
  const compareAt = row.compare_at_price ? String(row.compare_at_price) : "0";

  // Parse variants from JSONB
  const rawVariants = Array.isArray(row.variants) ? row.variants : [];
  const variantEdges = rawVariants.length > 0
    ? rawVariants.map((v: Record<string, unknown>) => {
        const variantInventory = typeof v.inventory_quantity === "number"
          ? v.inventory_quantity
          : typeof v.inventoryQuantity === "number"
            ? v.inventoryQuantity
            : null;
        const variantAvailable = variantInventory !== null
          ? variantInventory > 0
          : typeof v.available === "boolean"
            ? v.available
            : row.available_for_sale ?? false;

        return {
          node: {
            id: String(v.admin_graphql_api_id || v.id || row.shopify_id),
            title: String(v.title || "Default"),
            price: { amount: String(v.price ?? row.price ?? 0), currencyCode: currency },
            availableForSale: variantAvailable,
            inventoryQuantity: variantInventory,
            selectedOptions: Array.isArray(v.selectedOptions) ? v.selectedOptions as Array<{name: string; value: string}> : [],
          },
        };
      })
    : [
        {
          node: {
            id: `gid://shopify/ProductVariant/${row.shopify_id}`,
            title: "Default",
            price: { amount: price, currencyCode: currency },
            availableForSale: row.available_for_sale ?? true,
            inventoryQuantity: row.inventory_quantity ?? null,
            selectedOptions: [] as Array<{name: string; value: string}>,
          },
        },
      ];

  // Parse images from JSONB
  const rawImages = Array.isArray(row.images) ? row.images : [];
  const imageEdges = rawImages.length > 0
    ? rawImages.map((img: Record<string, unknown>) => ({
        node: { url: String(img.src || img.url || ""), altText: String(img.alt || "") },
      }))
    : row.image_url
      ? [{ node: { url: row.image_url, altText: null } }]
      : [];

  // Parse options from JSONB
  const rawOptions = Array.isArray(row.options) ? row.options : [];

  return {
    node: {
      id: `gid://shopify/Product/${row.shopify_id}`,
      title: row.title,
      description: row.description || "",
      handle: row.handle,
      vendor: row.vendor || undefined,
      priceRange: { minVariantPrice: { amount: price, currencyCode: currency } },
      compareAtPriceRange: { maxVariantPrice: { amount: compareAt, currencyCode: currency } },
      images: { edges: imageEdges },
      variants: { edges: variantEdges },
      options: rawOptions,
      tags: Array.isArray(row.tags) ? row.tags : [],
      inventoryQuantity: row.inventory_quantity ?? null,
    },
  };
}

const DB_PRODUCT_COLUMNS =
  "id, shopify_id, title, handle, description, vendor, price, compare_at_price, currency, image_url, images, variants, options, available_for_sale, inventory_quantity, tags" as const;

/**
 * Fetch products from shopify_products table by L1/L2/L3 tags.
 * Expands queries to include known variant tags for maximum recall.
 * Returns ShopifyProduct[] ready for use in grids.
 */
export async function fetchProductsByTags(opts: {
  l1?: string;
  l2?: string;
  l3?: string;
  limit?: number;
}): Promise<ShopifyProduct[]> {
  const { l1, l2, l3, limit = 100 } = opts;
  try {
    const { supabase } = await import("@/integrations/supabase/client");
    const { getL1Variants, getL2Variants, getL3Variants } = await import("@/lib/tagNormalization");

    // Build primary tag set (canonical)
    const tags: string[] = [];
    if (l1) tags.push(`L1:${l1}`);
    if (l2) tags.push(`L2:${l2}`);
    if (l3) tags.push(`L3:${l3}`);

    // Build variant tag sets for OR-based expansion
    const l1Variants = l1 ? getL1Variants(l1) : [];
    const l2Variants = l2 ? getL2Variants(l2) : [];
    const l3Variants = l3 ? getL3Variants(l3) : [];

    const hasVariants = l1Variants.length > 1 || l2Variants.length > 1 || l3Variants.length > 1;

    let query = supabase
      .from("shopify_products")
      .select(DB_PRODUCT_COLUMNS)
      .eq("status", "active");

    if (tags.length > 0) {
      if (hasVariants) {
        // Use overlaps for tags with known variants to catch all spellings
        // Build OR filter conditions for each level
        const orConditions: string[] = [];
        
        if (l3Variants.length > 1) {
          // Match any L3 variant AND the required L1
          const l3TagArrays = l3Variants.map(v => `L3:${v}`);
          const l1TagArrays = l1Variants.map(v => `L1:${v}`);
          query = query.overlaps("tags", l3TagArrays).overlaps("tags", l1TagArrays);
          if (l2) {
            const l2TagArrays = l2Variants.map(v => `L2:${v}`);
            query = query.overlaps("tags", l2TagArrays);
          }
        } else if (l2Variants.length > 1) {
          const l2TagArrays = l2Variants.map(v => `L2:${v}`);
          const l1TagArrays = l1Variants.map(v => `L1:${v}`);
          query = query.overlaps("tags", l2TagArrays).overlaps("tags", l1TagArrays);
        } else if (l1Variants.length > 1) {
          const l1TagArrays = l1Variants.map(v => `L1:${v}`);
          query = query.overlaps("tags", l1TagArrays);
          if (l2) query = query.contains("tags", [`L2:${l2}`]);
          if (l3) query = query.contains("tags", [`L3:${l3}`]);
        }
      } else {
        query = query.contains("tags", tags);
      }
    }

    const { data, error } = await query
      .order("available_for_sale", { ascending: false })
      .limit(limit);

    if (error) {
      console.error("fetchProductsByTags error:", error);
      return [];
    }

    return (data || []).map(dbToShopifyProduct);
  } catch (err) {
    console.error("fetchProductsByTags failed:", err);
    return [];
  }
}

/**
 * Lifestage-LP product fetch (valpfoder, seniorfoder, kattungefoder, seniorkattfoder).
 *
 * Bredare än enbart L3:<lifestage>-taggen — vi använder unionen av:
 *   - Den kanoniska `alder:<lifestage>`-taxonomin (livsfas-metafält → tag)
 *   - L3:<lifestage>-taggar (för bakåtkompabilitet med äldre kollektioner)
 * AND-filtrerat mot rätt L1 (art) OCH foder-L2 (foder|mat-godis), så att livsfas-tillbehör
 * (valpsele, kattungeleksak osv.) inte läcker in på en ren foder-LP.
 *
 * Stoppar "tunna stubbar" — t.ex. katt/foder/senior gick från 4 → 24 produkter
 * och hund/foder/valpfoder från 35 → 68 produkter med samma strikta art+foder-krav.
 */
export async function fetchLifestageFoodProducts(opts: {
  species: 'hund' | 'katt';
  lifestage: 'valp' | 'adult' | 'senior' | 'kattunge';
  limit?: number;
}): Promise<ShopifyProduct[]> {
  const { species, lifestage, limit = 50 } = opts;
  try {
    const { supabase } = await import("@/integrations/supabase/client");

    // Bygg union av lifestage-taggar (alder: + L3:-aliaser)
    const lifestageTags: string[] = [`alder:${lifestage}`];
    if (lifestage === 'valp') lifestageTags.push('L3:valpfoder');
    if (lifestage === 'kattunge') lifestageTags.push('L3:kattunge');
    if (lifestage === 'adult') {
      // 'vuxen' = svensk synonym; båda används i taxonomin
      lifestageTags.push('alder:vuxen', 'L3:vuxenfoder', 'L3:vuxen');
    }
    if (lifestage === 'senior') {
      // Hund-LP är döpt 'seniorfoder', katt-LP är döpt 'senior'
      lifestageTags.push('L3:seniorfoder', 'L3:senior');
    }

    const { data, error } = await supabase
      .from("shopify_products")
      .select(DB_PRODUCT_COLUMNS)
      .eq("status", "active")
      .contains("tags", [`L1:${species}`])
      .overlaps("tags", ['L2:foder', 'L2:mat-godis'])
      .overlaps("tags", lifestageTags)
      .order("available_for_sale", { ascending: false })
      .limit(limit);

    if (error) {
      console.error("fetchLifestageFoodProducts error:", error);
      return [];
    }
    return (data || []).map(dbToShopifyProduct);
  } catch (err) {
    console.error("fetchLifestageFoodProducts failed:", err);
    return [];
  }
}


/**
 * Search products from shopify_products table using full-text search (FTS).
 * Falls back to ilike if FTS returns no results.
 */
export async function searchProductsFromDB(searchQuery: string, limit = 100): Promise<ShopifyProduct[]> {
  if (!searchQuery.trim()) return [];
  try {
    const { supabase } = await import("@/integrations/supabase/client");
    const trimmed = searchQuery.trim();

    // Try full-text search first (uses GIN index)
    const { data: ftsData, error: ftsError } = await supabase
      .rpc("search_products_fts", { search_query: trimmed, result_limit: limit });

    if (!ftsError && ftsData && ftsData.length > 0) {
      return ftsData.map(dbToShopifyProduct);
    }

    // Fallback to ilike for partial matches (e.g. "torrf" matching "torrfoder")
    const q = `%${trimmed}%`;
    const { data, error } = await supabase
      .from("shopify_products")
      .select(DB_PRODUCT_COLUMNS)
      .eq("status", "active")
      .or(`title.ilike.${q},vendor.ilike.${q}`)
      .order("available_for_sale", { ascending: false })
      .limit(limit);

    if (error) {
      console.error("searchProductsFromDB ilike fallback error:", error);
      return [];
    }

    return (data || []).map(dbToShopifyProduct);
  } catch (err) {
    console.error("searchProductsFromDB failed:", err);
    return [];
  }
}

/**
 * Fetch products from DB by collection handle (parses L1/L2/L3 from handle).
 */
export async function fetchCollectionProductsFromDB(handle: string, limit = 100): Promise<ShopifyProduct[]> {
  const parts = handle.split("-");
  const l1 = parts[0];

  if (!(KNOWN_L1_SLUGS as readonly string[]).includes(l1)) {
    // Not a category handle, search by title
    return searchProductsFromDB(handle, limit);
  }

  // Known L2 slugs to detect handle structure
  const KNOWN_L2_SLUGS = new Set([
    'mat-godis', 'leksaker', 'sova-vila', 'koppel-halsband', 'godis-tugg',
    'klosmobler', 'toalett-hygien', 'sova-mysa', 'transport', 'vard-halsa',
    'burar-inredning', 'stro-hygien', 'mat-foder', 'terrarier-teknik',
    'akvarier-teknik', 'fiskmat', 'inredning', 'leksaker-aktivering',
    'foder', 'godis-snacks', 'tugg-dental', 'matplats', 'klader-uteliv',
    'transport-sakerhet', 'rykt-vard', 'foder-godis', 'ridning-utrustning',
    'stall-hagn', 'vard-tillbehor', 'tillbehor',
  ]);

  // Parse L2 and L3
  let l2 = '';
  let l3 = '';
  for (let i = parts.length; i >= 2; i--) {
    const candidate = parts.slice(1, i).join('-');
    if (KNOWN_L2_SLUGS.has(candidate)) {
      l2 = candidate;
      l3 = i < parts.length ? parts.slice(i).join('-') : '';
      break;
    }
  }
  if (!l2 && parts.length >= 2) {
    l2 = parts.slice(1).join('-');
  }

  // Try with L3 first, then L2 only
  if (l3) {
    const results = await fetchProductsByTags({ l1, l2, l3, limit });
    if (results.length > 0) return results;
    // Fallback: try L1+L3 without L2
    const fallback = await fetchProductsByTags({ l1, l3, limit });
    if (fallback.length > 0) return fallback;
  }

  return fetchProductsByTags({ l1, l2: l2 || undefined, limit });
}

// ---------------------------------------------------------------------------
// Server-side paginated/sorted/filtered product listing
// ---------------------------------------------------------------------------

// Re-export från `productSort` så att alla tidigare imports
// (`import type { ProductsSort } from "@/lib/shopify"`) fortsätter fungera,
// medan källan-till-sanning lever i ett ställe.
export {
  PRODUCT_SORTS,
  PRODUCT_SORT_LABELS,
  isProductsSort,
  coerceProductsSort,
  type ProductsSort,
} from "./productSort";
import { coerceProductsSort as _coerceProductsSort, type ProductsSort as _ProductsSort, applyProductsSortOrder } from "./productSort";

export interface ProductsPageOpts {
  collectionHandle?: string;
  query?: string;
  brands?: string[];
  inStockOnly?: boolean;
  priceMin?: number;
  priceMax?: number;
  /** UI ids from ShopifyProductFilters: 'puppy' | 'adult' | 'senior'. */
  petAge?: string[];
  /** UI ids from ShopifyProductFilters: 'dry-food' | 'wet-food' | 'treats' | ... */
  productTypes?: string[];
  /** UI ids from ShopifyProductFilters: 'chicken' | 'beef' | 'salmon' | ... */
  flavors?: string[];
  page: number;
  pageSize: number;
  sort?: _ProductsSort;
  /** Hard cap on the total result set; defaults to 2000. */
  maxResults?: number;
}

/**
 * Maps the hard-coded UI filter ids in ShopifyProductFilters.tsx to the actual
 * tag values that exist on shopify_products. Each UI id may match several tag
 * variants — they are OR'd within a dimension and AND'd across dimensions so
 * the same filter conditions apply to BOTH the count and every page.
 *
 * Keep this in sync with ShopifyProductFilters.tsx option ids.
 */
const PET_AGE_TAG_MAP: Record<string, string[]> = {
  puppy: ["alder:valp", "alder:kattunge", "alder:junior", "alder:unghund", "valp", "kattunge"],
  adult: ["alder:vuxen", "alder:vuxen hund", "alder:vuxen katt", "vuxen"],
  senior: ["alder:senior", "senior"],
};
const PRODUCT_TYPE_TAG_MAP: Record<string, string[]> = {
  "dry-food": ["L3:torrfoder", "torrfoder"],
  "wet-food": ["L3:vatfoder", "L3:våtfoder", "vatfoder", "våtfoder"],
  "treats": [
    "L3:godis", "L3:tugg", "L2:godis-snacks", "L2:godis-tugg",
    "godis-snacks", "godis-tugg", "godis", "tugg", "beloningsgodis",
  ],
  "toys": [
    "L3:leksaker", "L2:leksaker", "L2:leksaker-aktivering",
    "L3:leksaker-aktivering", "leksaker",
  ],
  "beds": ["L3:sangar", "L2:sova-vila", "L2:sova-mysa", "sangar", "sängar", "sova-vila"],
  "leash": [
    "L3:koppel", "L3:halsband", "L2:koppel-halsband",
    "koppel", "halsband", "koppel-halsband",
  ],
  "grooming": [
    "L3:palsvard", "L3:pälsvård", "L2:rykt-vard", "L2:vard-halsa",
    "palsvard", "pälsvård", "rykt-vard",
  ],
  "accessories": [
    "L2:tillbehor", "L3:tillbehor", "L2:vard-tillbehor", "tillbehor", "tillbehör",
  ],
};
const FLAVOR_TAG_MAP: Record<string, string[]> = {
  chicken: ["smak:kyckling"],
  beef: ["smak:not", "smak:nöt", "smak:nötkött", "smak:notkott", "smak:biff"],
  salmon: ["smak:lax"],
  lamb: ["smak:lamm"],
  duck: ["smak:anka"],
  fish: ["smak:fisk", "smak:tonfisk"],
  venison: ["smak:vilt", "smak:hjort"],
  mixed: ["smak:blandad", "smak:blandat"],
};

function expandTagIds(ids: string[], map: Record<string, string[]>): string[] {
  const out = new Set<string>();
  for (const id of ids) {
    const tags = map[id];
    if (tags) tags.forEach((t) => out.add(t));
  }
  return [...out];
}


export interface ProductsPageResult {
  products: ShopifyProduct[];
  total: number;
  availableBrands: string[];
}

const KNOWN_L2_HANDLE_SLUGS = new Set([
  "mat-godis", "leksaker", "sova-vila", "koppel-halsband", "godis-tugg",
  "klosmobler", "toalett-hygien", "sova-mysa", "transport", "vard-halsa",
  "burar-inredning", "stro-hygien", "mat-foder", "terrarier-teknik",
  "akvarier-teknik", "fiskmat", "inredning", "leksaker-aktivering",
  "foder", "godis-snacks", "tugg-dental", "matplats", "klader-uteliv",
  "transport-sakerhet", "rykt-vard", "foder-godis", "ridning-utrustning",
  "stall-hagn", "vard-tillbehor", "tillbehor",
]);

interface ParsedScope {
  l1?: string;
  l2?: string;
  l3?: string;
}

function parseHandleScope(handle: string): ParsedScope | null {
  const parts = handle.split("-");
  const l1 = parts[0];
  if (!(KNOWN_L1_SLUGS as readonly string[]).includes(l1)) return null;
  let l2 = "";
  let l3 = "";
  for (let i = parts.length; i >= 2; i--) {
    const candidate = parts.slice(1, i).join("-");
    if (KNOWN_L2_HANDLE_SLUGS.has(candidate)) {
      l2 = candidate;
      l3 = i < parts.length ? parts.slice(i).join("-") : "";
      break;
    }
  }
  if (!l2 && parts.length >= 2) l2 = parts.slice(1).join("-");
  return { l1, l2: l2 || undefined, l3: l3 || undefined };
}

/**
 * Apply L1/L2/L3 tag scope to a Supabase query, expanding known tag variants.
 * Mirrors the logic in fetchProductsByTags so listing & pagination see the same set.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function applyTagScope(query: any, scope: ParsedScope): Promise<any> {
  const { getL1Variants, getL2Variants, getL3Variants } = await import("@/lib/tagNormalization");
  const l1Variants = scope.l1 ? getL1Variants(scope.l1) : [];
  const l2Variants = scope.l2 ? getL2Variants(scope.l2) : [];
  const l3Variants = scope.l3 ? getL3Variants(scope.l3) : [];
  const hasVariants = l1Variants.length > 1 || l2Variants.length > 1 || l3Variants.length > 1;

  let q = query;
  if (!scope.l1 && !scope.l2 && !scope.l3) return q;

  if (hasVariants) {
    if (l3Variants.length > 1) {
      q = q.overlaps("tags", l3Variants.map(v => `L3:${v}`));
      if (scope.l1) q = q.overlaps("tags", l1Variants.map(v => `L1:${v}`));
      if (scope.l2) q = q.overlaps("tags", l2Variants.map(v => `L2:${v}`));
    } else if (l2Variants.length > 1) {
      q = q.overlaps("tags", l2Variants.map(v => `L2:${v}`));
      if (scope.l1) q = q.overlaps("tags", l1Variants.map(v => `L1:${v}`));
    } else if (l1Variants.length > 1) {
      q = q.overlaps("tags", l1Variants.map(v => `L1:${v}`));
      if (scope.l2) q = q.contains("tags", [`L2:${scope.l2}`]);
      if (scope.l3) q = q.contains("tags", [`L3:${scope.l3}`]);
    }
  } else {
    const tags: string[] = [];
    if (scope.l1) tags.push(`L1:${scope.l1}`);
    if (scope.l2) tags.push(`L2:${scope.l2}`);
    if (scope.l3) tags.push(`L3:${scope.l3}`);
    if (tags.length > 0) q = q.contains("tags", tags);
  }
  return q;
}

/**
 * Server-side product listing. Performs scope + filter + sort + pagination
 * + total count in a single Supabase round-trip, plus one extra small query
 * for the available-brands list (scoped, but ignoring user filters so the
 * brand picker stays stable).
 */
export async function fetchProductsPaginated(opts: ProductsPageOpts): Promise<ProductsPageResult> {
  // ── Cache layer (in-memory TTL+LRU + in-flight dedup) ─────────────────
  // Returns a cached result for identical opts within TTL; coalesces
  // concurrent identical calls into one DB round-trip. Bust is driven by
  // invalidateInventoryCaches() → clearProductsPageCache().
  const {
    getCachedProductsPage,
    setCachedProductsPage,
    getInflightProductsPage,
    setInflightProductsPage,
    getCachedBrands,
    setCachedBrands,
    getInflightBrands,
    setInflightBrands,
  } = await import("@/lib/productsPageCache");

  const cached = getCachedProductsPage(opts);
  if (cached) return cached;
  const inflight = getInflightProductsPage(opts);
  if (inflight) return inflight;

  const exec = async (): Promise<ProductsPageResult> => {
  const {
    collectionHandle,
    query: searchQuery,
    brands = [],
    inStockOnly = false,
    priceMin,
    priceMax,
    petAge = [],
    productTypes = [],
    flavors = [],
    page,
    pageSize,
    sort: rawSort = "default",
    maxResults = 2000,
  } = opts;
  // API-side validering: alla värden som inte tillhör PRODUCT_SORTS faller
  // tyst tillbaka till "default" så att en trasig URL/edge-fn-payload aldrig
  // kraschar fetchen eller hamnar i switchens default-gren med fel index.
  const sort: _ProductsSort = _coerceProductsSort(rawSort);


  try {
    const { supabase } = await import("@/integrations/supabase/client");

    let scope: ParsedScope | null = null;
    if (collectionHandle) scope = parseHandleScope(collectionHandle);

    // Build a query with scope + free-text search applied (no user filters).
    const buildScoped = async (selectCols: string, withCount: boolean) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let q: any = supabase
        .from("shopify_products")
        .select(selectCols, withCount ? { count: "exact" } : undefined)
        .eq("status", "active");
      if (scope) {
        q = await applyTagScope(q, scope);
      } else if (collectionHandle && !scope) {
        const term = `%${collectionHandle.replace(/[%_]/g, "")}%`;
        q = q.or(`title.ilike.${term},vendor.ilike.${term}`);
      }
      if (searchQuery && searchQuery.trim()) {
        const term = `%${searchQuery.trim().replace(/[%_]/g, "")}%`;
        q = q.or(`title.ilike.${term},vendor.ilike.${term}`);
      }
      return q;
    };

    // Brand list (scope only, no user filters) — independently cached so
    // the vendor picker stays instant even when filter/sort/page churn.
    let availableBrands = getCachedBrands(opts) ?? [];
    if (availableBrands.length === 0) {
      const brandInflight = getInflightBrands(opts);
      if (brandInflight) {
        availableBrands = await brandInflight;
      } else {
        const brandPromise = (async () => {
          const brandQuery = await buildScoped("vendor", false);
          const { data: brandRows } = await brandQuery.limit(maxResults);
          const brandSet = new Map<string, string>();
          for (const row of (brandRows as Array<{ vendor: string | null }> | null) || []) {
            if (row?.vendor) {
              const k = row.vendor.toLowerCase();
              if (!brandSet.has(k)) brandSet.set(k, row.vendor);
            }
          }
          const list = Array.from(brandSet.values()).sort((a, b) => a.localeCompare(b, "sv"));
          setCachedBrands(opts, list);
          return list;
        })();
        availableBrands = await setInflightBrands(opts, brandPromise);
      }
    }

    // Filtered + sorted + paginated query (single roundtrip thanks to count:'exact')
    let listQ = await buildScoped(DB_PRODUCT_COLUMNS, true);

    if (typeof priceMin === "number") listQ = listQ.gte("price", priceMin);
    if (typeof priceMax === "number") listQ = listQ.lte("price", priceMax);
    if (inStockOnly) listQ = listQ.gt("inventory_quantity", 0);
    if (brands.length > 0) listQ = listQ.in("vendor", brands);

    // Tag-based filter dimensions: each is OR within, AND across, so the
    // count and every page see the SAME filtered set under the same sort.
    const ageTags = expandTagIds(petAge, PET_AGE_TAG_MAP);
    const typeTags = expandTagIds(productTypes, PRODUCT_TYPE_TAG_MAP);
    const flavorTags = expandTagIds(flavors, FLAVOR_TAG_MAP);
    if (ageTags.length > 0) listQ = listQ.overlaps("tags", ageTags);
    if (typeTags.length > 0) listQ = listQ.overlaps("tags", typeTags);
    if (flavorTags.length > 0) listQ = listQ.overlaps("tags", flavorTags);

    // Single source of truth for sort ORDER BY-chain — delas med
    // integrationstesterna i productsPaginationSort.integration.test.ts.
    listQ = applyProductsSortOrder(listQ, sort);

    const safePageSize = Math.max(1, Math.min(pageSize, 100));
    const safePage = Math.max(1, page);
    const from = (safePage - 1) * safePageSize;
    const to = Math.min(from + safePageSize - 1, maxResults - 1);
    listQ = listQ.range(from, to);

    const { data, error, count } = await listQ;
    if (error) {
      console.error("fetchProductsPaginated error:", error);
      // Don't cache failures — let the next call retry against the DB.
      return { products: [], total: 0, availableBrands };
    }

    const total = Math.min(count ?? 0, maxResults);
    const products = (data || []).map(dbToShopifyProduct);
    const result: ProductsPageResult = { products, total, availableBrands };
    setCachedProductsPage(opts, result);
    return result;
  } catch (err) {
    console.error("fetchProductsPaginated failed:", err);
    return { products: [], total: 0, availableBrands: [] };
  }
  };

  return setInflightProductsPage(opts, exec());
}

export interface CheckoutBuyerIdentity {
  email?: string;
  phone?: string;
  deliveryAddressPreferences?: Array<{
    deliveryAddress: {
      firstName?: string;
      lastName?: string;
      address1?: string;
      city?: string;
      zip?: string;
      country?: string;
    };
  }>;
}

export interface CartAttribute {
  key: string;
  value: string;
}

const CART_ATTRIBUTES_UPDATE = `
  mutation cartAttributesUpdate($cartId: ID!, $attributes: [AttributeInput!]!) {
    cartAttributesUpdate(cartId: $cartId, attributes: $attributes) {
      cart {
        id
        attributes { key value }
      }
      userErrors { field message }
    }
  }
`;

/**
 * Attach attributes (e.g. _gclid, _ga_client_id) to an existing Shopify cart.
 * These flow through to the Shopify Order as `note_attributes` and survive
 * the entire checkout flow — including webhook payloads.
 */
export async function updateCartAttributes(
  cartId: string,
  attributes: CartAttribute[]
): Promise<boolean> {
  return verifyCartAttributes(cartId, attributes);
}

export async function verifyCartAttributes(
  cartId: string,
  attributes: CartAttribute[]
): Promise<boolean> {
  if (!attributes.length) return true;
  try {
    const data = await storefrontApiRequest(CART_ATTRIBUTES_UPDATE, { cartId, attributes });
    const errors = data?.data?.cartAttributesUpdate?.userErrors || [];
    if (errors.length > 0) {
      console.warn('cartAttributesUpdate userErrors:', errors);
      return false;
    }
    const returnedAttrs: CartAttribute[] = data?.data?.cartAttributesUpdate?.cart?.attributes || [];
    const returned = new Map(returnedAttrs.map((attr) => [attr.key, attr.value]));
    const required = attributes.filter((attr) => attr.key === '_gclid' || attr.key === '_gbraid' || attr.key === '_wbraid' || attr.key === '_google_click_id' || attr.key === '_google_click_id_type' || attr.key === '_ga_client_id' || attr.key === '_ga_session_id' || attr.key === '_session_id');
    const verified = required.every((attr) => returned.get(attr.key) === attr.value);
    if (!verified) {
      console.warn('cartAttributesUpdate verification failed:', required.map((attr) => attr.key));
      return false;
    }
    return true;
  } catch (e) {
    console.warn('cartAttributesUpdate exception:', e);
    return false;
  }
}

// ─── Shopify Cart API helpers ───

/**
 * Canonical checkout host. We always rewrite Shopify-issued checkoutUrl values
 * to `checkout.zooi.se` so customers never see the apex (zooi.se) or the
 * permanent myshopify.com host on the payment page. The apex is reserved for
 * the storefront; checkout.zooi.se is mapped to Shopify's checkout edge.
 */
export const CHECKOUT_HOST = 'checkout.zooi.se';

function formatCheckoutUrl(checkoutUrl: string): string {
  try {
    const url = new URL(checkoutUrl);
    if (url.hostname !== CHECKOUT_HOST) {
      url.hostname = CHECKOUT_HOST;
      url.protocol = 'https:';
      url.port = '';
    }
    url.searchParams.set('channel', 'online_store');
    return url.toString();
  } catch {
    return checkoutUrl;
  }
}

function isCartNotFoundError(userErrors: Array<{ field: string[] | null; message: string }>): boolean {
  return userErrors.some(e => e.message.toLowerCase().includes('cart not found') || e.message.toLowerCase().includes('does not exist'));
}

/** Ensure variant ID is in Shopify GID format */
function toVariantGid(id: string | number): string {
  const s = String(id);
  if (s.startsWith('gid://')) return s;
  return `gid://shopify/ProductVariant/${s}`;
}

export async function createShopifyCart(item: CartItem): Promise<{ cartId: string; checkoutUrl: string; lineId: string } | null> {
  const merchandiseId = toVariantGid(item.variantId);
  const data = await storefrontApiRequest(CART_CREATE_MUTATION, {
    input: { lines: [{ quantity: item.quantity, merchandiseId }] },
  });

  const logCheckoutFailure = (reason: string, extra?: Record<string, unknown>) => {
    try {
      void fetch(`https://nokhdcatincbbvpdkrjk.supabase.co/functions/v1/log-attribution-event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_name: 'checkout.cart_create_failed',
          status: 'failed',
          platform: 'shopify',
          payload_summary: { reason, variant: merchandiseId, ...extra },
          error_message: reason,
          suggested_action: 'Investigate Shopify Storefront cartCreate failures',
        }),
        keepalive: true,
      });
    } catch { /* noop */ }
  };

  if (!data) {
    console.error('Cart creation failed: no response from Storefront API');
    logCheckoutFailure('no_storefront_response');
    return null;
  }

  const userErrors = data.data?.cartCreate?.userErrors || [];
  if (userErrors.length > 0) {
    console.error('Cart creation failed:', userErrors);
    logCheckoutFailure('user_errors', { errors: userErrors.slice(0, 3) });
    return null;
  }

  const cart = data.data?.cartCreate?.cart;
  if (!cart?.checkoutUrl) {
    logCheckoutFailure('missing_checkout_url');
    return null;
  }

  const lineId = cart.lines.edges[0]?.node?.id;
  if (!lineId) {
    logCheckoutFailure('missing_line_id');
    return null;
  }

  return { cartId: cart.id, checkoutUrl: formatCheckoutUrl(cart.checkoutUrl), lineId };
}

export async function addLineToShopifyCart(cartId: string, item: CartItem): Promise<{ success: boolean; lineId?: string; cartNotFound?: boolean }> {
  const merchandiseId = toVariantGid(item.variantId);
  const data = await storefrontApiRequest(CART_LINES_ADD_MUTATION, {
    cartId,
    lines: [{ quantity: item.quantity, merchandiseId }],
  });

  if (!data) { console.error('Add line failed: no API response'); return { success: false }; }

  const userErrors = data.data?.cartLinesAdd?.userErrors || [];
  if (isCartNotFoundError(userErrors)) return { success: false, cartNotFound: true };
  if (userErrors.length > 0) { console.error('Add line failed:', userErrors); return { success: false }; }

  const lines = data.data?.cartLinesAdd?.cart?.lines?.edges || [];
  const newLine = lines.find((l: { node: { id: string; merchandise: { id: string } } }) => l.node.merchandise.id === merchandiseId);
  return { success: true, lineId: newLine?.node?.id };
}

export async function updateShopifyCartLine(cartId: string, lineId: string, quantity: number): Promise<{ success: boolean; cartNotFound?: boolean }> {
  const data = await storefrontApiRequest(CART_LINES_UPDATE_MUTATION, {
    cartId,
    lines: [{ id: lineId, quantity }],
  });

  if (!data) { console.error('Update line failed: no API response'); return { success: false }; }

  const userErrors = data.data?.cartLinesUpdate?.userErrors || [];
  if (isCartNotFoundError(userErrors)) return { success: false, cartNotFound: true };
  if (userErrors.length > 0) { console.error('Update line failed:', userErrors); return { success: false }; }
  return { success: true };
}

export async function removeLineFromShopifyCart(cartId: string, lineId: string): Promise<{ success: boolean; cartNotFound?: boolean }> {
  const data = await storefrontApiRequest(CART_LINES_REMOVE_MUTATION, {
    cartId,
    lineIds: [lineId],
  });

  if (!data) { console.error('Remove line failed: no API response'); return { success: false }; }

  const userErrors = data.data?.cartLinesRemove?.userErrors || [];
  if (isCartNotFoundError(userErrors)) return { success: false, cartNotFound: true };
  if (userErrors.length > 0) { console.error('Remove line failed:', userErrors); return { success: false }; }
  return { success: true };
}


export interface ShopifyCartLine {
  lineId: string;
  variantId: string;
  quantity: number;
}

export async function fetchShopifyCart(cartId: string): Promise<{ exists: boolean; checkoutUrl?: string; lines?: ShopifyCartLine[] }> {
  try {
    const data = await storefrontApiRequest(CART_QUERY, { id: cartId });
    if (!data) return { exists: false };
    const cart = data?.data?.cart;
    if (!cart || cart.totalQuantity === 0) return { exists: false };

    const lines: ShopifyCartLine[] = (cart.lines?.edges || []).map((edge: any) => ({
      lineId: edge.node.id,
      variantId: edge.node.merchandise?.id,
      quantity: edge.node.quantity,
    }));

    return {
      exists: true,
      checkoutUrl: cart.checkoutUrl ? formatCheckoutUrl(cart.checkoutUrl) : undefined,
      lines,
    };
  } catch {
    return { exists: false };
  }
}

export async function updateCartBuyerIdentity(
  cartId: string,
  buyerIdentity: CheckoutBuyerIdentity
): Promise<string | null> {
  try {
    const data = await storefrontApiRequest(CART_BUYER_IDENTITY_UPDATE, {
      cartId,
      buyerIdentity: {
        email: buyerIdentity.email,
        phone: buyerIdentity.phone,
        deliveryAddressPreferences: buyerIdentity.deliveryAddressPreferences,
        countryCode: 'SE',
      },
    });

    if (!data) return null;
    const userErrors = data.data?.cartBuyerIdentityUpdate?.userErrors || [];
    if (userErrors.length > 0) {
      console.error('Buyer identity update failed:', userErrors);
      return null;
    }

    const checkoutUrl = data.data?.cartBuyerIdentityUpdate?.cart?.checkoutUrl;
    return checkoutUrl ? formatCheckoutUrl(checkoutUrl) : null;
  } catch (error) {
    console.error('Error updating buyer identity:', error);
    gtmException('buyer_identity_update_failed', false);
    return null;
  }
}

// ─── Selected delivery options (riktig shipping line) ─────────────

export interface CartDeliveryOption {
  handle: string;
  title: string | null;
  code: string | null;
  deliveryMethodType: string | null;
  estimatedCost: { amount: string; currencyCode: string } | null;
}

export interface CartDeliveryGroup {
  id: string;
  groupType: string | null;
  selectedDeliveryOption: CartDeliveryOption | null;
  deliveryOptions: CartDeliveryOption[];
}

/**
 * Prefix på service_code för rates som kommer från vår egen carrier service.
 * MÅSTE matcha ZOOI_RATE_PREFIX i supabase/functions/shopify-carrier-rates/rate-logic.ts.
 * Allt utan detta prefix (t.ex. Shipmondo live-rates) filtreras bort så att
 * UI och kassan bara ser Zooi-priser.
 */
export const ZOOI_RATE_PREFIX = 'zooi-';

export function isZooiDeliveryOption(option: CartDeliveryOption | null | undefined): boolean {
  if (!option) return false;
  const code = (option.code ?? '').toLowerCase();
  const handle = (option.handle ?? '').toLowerCase();
  return code.startsWith(ZOOI_RATE_PREFIX) || handle.startsWith(ZOOI_RATE_PREFIX);
}

function filterToZooiOnly(groups: CartDeliveryGroup[]): CartDeliveryGroup[] {
  return groups.map((g) => {
    const zooi = g.deliveryOptions.filter(isZooiDeliveryOption);
    const dropped = g.deliveryOptions.length - zooi.length;
    if (dropped > 0) {
      console.warn(`[shipping] Filtered out ${dropped} non-zooi delivery option(s) in group ${g.id}`);
    }
    const selected = isZooiDeliveryOption(g.selectedDeliveryOption) ? g.selectedDeliveryOption : null;
    return { ...g, deliveryOptions: zooi, selectedDeliveryOption: selected };
  });
}

/**
 * Hämtar deliveryGroups med carrier-rates för en cart. Kräver att carten
 * har en buyerIdentity med leveransadress (annars returneras tom lista).
 * Returnerar endast rates som kommer från vår carrier service (zooi-prefix).
 */
export async function fetchCartDeliveryGroups(cartId: string): Promise<CartDeliveryGroup[]> {
  const QUERY = `
    query cartDelivery($id: ID!) {
      cart(id: $id) {
        id
        deliveryGroups(first: 10, withCarrierRates: true) {
          edges {
            node {
              id
              groupType
              selectedDeliveryOption {
                handle title code deliveryMethodType
                estimatedCost { amount currencyCode }
              }
              deliveryOptions {
                handle title code deliveryMethodType
                estimatedCost { amount currencyCode }
              }
            }
          }
        }
      }
    }
  `;
  try {
    const data = await storefrontApiRequest(QUERY, { id: cartId });
    const edges = data?.data?.cart?.deliveryGroups?.edges ?? [];
    const groups = edges.map((e: { node: CartDeliveryGroup }) => e.node);
    return filterToZooiOnly(groups);
  } catch (e) {
    console.warn('fetchCartDeliveryGroups failed:', e);
    return [];
  }
}

/**
 * Sätter en minimal placeholder-buyerIdentity (SE + postnummer) så att
 * Shopify räknar fram deliveryGroups innan kunden fyllt i sin riktiga adress.
 * No-op om carten redan har en adress.
 */
export async function ensureCartHasShippingAddress(
  cartId: string,
  opts: { postalCode?: string } = {}
): Promise<boolean> {
  try {
    const groups = await fetchCartDeliveryGroups(cartId);
    if (groups.length > 0) return true; // redan satt

    const data = await storefrontApiRequest(CART_BUYER_IDENTITY_UPDATE, {
      cartId,
      buyerIdentity: {
        countryCode: 'SE',
        deliveryAddressPreferences: [
          {
            deliveryAddress: {
              country: 'SE',
              zip: opts.postalCode ?? '11122',
              city: 'Stockholm',
              address1: 'Placeholder 1',
              firstName: 'Placeholder',
              lastName: 'Customer',
            },
          },
        ],
      },
    });
    const errs = data?.data?.cartBuyerIdentityUpdate?.userErrors ?? [];
    if (errs.length) {
      console.warn('ensureCartHasShippingAddress userErrors:', errs);
      return false;
    }
    return true;
  } catch (e) {
    console.warn('ensureCartHasShippingAddress exception:', e);
    return false;
  }
}

/**
 * Tvingar Shopify-cart att använda en specifik delivery option per grupp
 * (= riktig shipping line i kassan). Om carten saknar deliveryGroups
 * (ingen adress än) sätter vi en placeholder först.
 *
 * Returnerar de uppdaterade deliveryGroups så att UI:t kan läsa Shopifys
 * faktiska pris (master-of-truth).
 */
export async function selectCartDeliveryOptions(
  cartId: string,
  selections: Array<{ deliveryGroupId: string; deliveryOptionHandle: string }>
): Promise<{ success: boolean; deliveryGroups: CartDeliveryGroup[]; userErrors: Array<{ field: string[] | null; message: string }> }> {
  try {
    const data = await storefrontApiRequest(CART_SELECTED_DELIVERY_OPTIONS_UPDATE, {
      cartId,
      selectedDeliveryOptions: selections.map((s) => ({
        deliveryGroupId: s.deliveryGroupId,
        deliveryOptionHandle: s.deliveryOptionHandle,
      })),
    });
    const userErrors = data?.data?.cartSelectedDeliveryOptionsUpdate?.userErrors ?? [];
    const edges = data?.data?.cartSelectedDeliveryOptionsUpdate?.cart?.deliveryGroups?.edges ?? [];
    const groups: CartDeliveryGroup[] = filterToZooiOnly(
      edges.map((e: { node: CartDeliveryGroup }) => e.node)
    );
    if (userErrors.length) {
      console.warn('cartSelectedDeliveryOptionsUpdate userErrors:', userErrors);
      return { success: false, deliveryGroups: groups, userErrors };
    }
    return { success: true, deliveryGroups: groups, userErrors: [] };
  } catch (e) {
    console.warn('selectCartDeliveryOptions exception:', e);
    return { success: false, deliveryGroups: [], userErrors: [{ field: null, message: String(e) }] };
  }
}

/**
 * Hög-nivå hjälpare: matcha vårt UI-shipping-id (postnord-ombud, postnord-paketbox,
 * postnord-hemleverans) till Shopifys deliveryOption.handle/code och tvinga valet.
 *
 * Strategi:
 *   1. Hämta deliveryGroups (sätt placeholder-adress vid behov).
 *   2. För varje grupp: hitta första option vars handle/code/title innehåller
 *      vårt id-suffix (t.ex. "ombud", "paketbox", "hemleverans").
 *   3. Skicka cartSelectedDeliveryOptionsUpdate.
 */
export async function applyShippingChoiceToShopifyCart(
  cartId: string,
  uiShippingId: string,
  opts: { postalCode?: string } = {}
): Promise<{
  success: boolean;
  matchedHandle: string | null;
  shopifyPriceSek: number | null;
  serviceCode: string | null;
  reason?: string;
  deliveryGroups: CartDeliveryGroup[];
}> {
  await ensureCartHasShippingAddress(cartId, opts);
  const groups = await fetchCartDeliveryGroups(cartId);
  if (!groups.length) {
    return { success: false, matchedHandle: null, shopifyPriceSek: null, serviceCode: null, reason: 'no_delivery_groups', deliveryGroups: [] };
  }

  const idLower = uiShippingId.toLowerCase();
  // Map UI-id → sökord vi förväntar oss i Shopifys handle/code/title
  const keyword = idLower.includes('paketbox') || idLower.includes('locker')
    ? ['paketbox', 'locker', 'parcel-locker', 'parcellocker']
    : idLower.includes('hemleverans') || idLower.includes('home')
    ? ['hemleverans', 'home', 'mypack-home']
    : ['ombud', 'service-point', 'servicepoint', 'mypack-collect'];

  const selections: Array<{ deliveryGroupId: string; deliveryOptionHandle: string }> = [];
  let matchedHandle: string | null = null;
  let shopifyPriceSek: number | null = null;
  let serviceCode: string | null = null;

  for (const group of groups) {
    const haystack = (o: CartDeliveryOption) =>
      `${o.handle} ${o.code ?? ''} ${o.title ?? ''}`.toLowerCase();
    const priceOf = (o: CartDeliveryOption) =>
      o.estimatedCost?.amount ? parseFloat(o.estimatedCost.amount) : NaN;

    // Fallback: om någon Zooi-rate i gruppen är 0 kr (fri frakt), välj den
    // automatiskt — fri frakt ska alltid vinna över betalda alternativ.
    // fetchCartDeliveryGroups har redan filtrerat bort allt utan zooi-prefix,
    // så alla deliveryOptions här kommer från vår carrier service.
    const freeOption = group.deliveryOptions.find((o) => {
      const p = priceOf(o);
      return Number.isFinite(p) && p <= 0;
    });

    const keywordMatch = group.deliveryOptions.find((o) => keyword.some((k) => haystack(o).includes(k)));
    const match = freeOption ?? keywordMatch ?? group.selectedDeliveryOption ?? group.deliveryOptions[0];
    if (!match) continue;
    if (freeOption && keywordMatch && freeOption.handle !== keywordMatch.handle) {
      console.info(
        `[shipping] Free Zooi-rate available (${freeOption.code ?? freeOption.handle}) — overriding user choice "${uiShippingId}".`
      );
    }
    selections.push({ deliveryGroupId: group.id, deliveryOptionHandle: match.handle });
    if (!matchedHandle) {
      matchedHandle = match.handle;
      shopifyPriceSek = match.estimatedCost?.amount ? parseFloat(match.estimatedCost.amount) : null;
      serviceCode = match.code ?? null;
    }
  }

  if (!selections.length) {
    return { success: false, matchedHandle: null, shopifyPriceSek: null, serviceCode: null, reason: 'no_match', deliveryGroups: groups };
  }

  const result = await selectCartDeliveryOptions(cartId, selections);
  // Plocka Shopifys pris från det valda alternativet i första gruppen efter update
  const updatedSelected = result.deliveryGroups[0]?.selectedDeliveryOption;
  if (updatedSelected) {
    matchedHandle = updatedSelected.handle;
    shopifyPriceSek = updatedSelected.estimatedCost?.amount ? parseFloat(updatedSelected.estimatedCost.amount) : shopifyPriceSek;
    serviceCode = updatedSelected.code ?? serviceCode;
  }
  return {
    success: result.success,
    matchedHandle,
    shopifyPriceSek,
    serviceCode,
    reason: result.success ? undefined : 'mutation_failed',
    deliveryGroups: result.deliveryGroups,
  };
}
